import { DECIMALS, MINOR_PER_UNIT } from './asset.js';
import { PRICE_UNIT_GAJU } from './price.js';
import { divideFloor } from './rounding.js';
/**
 * Turning exact integers into text a person reads.
 *
 * This is the only place in the package where precision is deliberately lost,
 * and it happens once, at the very edge. Everything upstream stays exact.
 *
 * Rounding here is always downward on amounts a user receives, so a screen
 * never promises more than the ledger will deliver. A number that reads
 * "$12.40" is backed by at least $12.40.
 */
function withThousandsSeparators(digits) {
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
/** Split a minor-unit amount into whole and fractional parts, sign removed. */
function split(amount, perUnit) {
    const negative = amount < 0n;
    const magnitude = negative ? -amount : amount;
    return { negative, whole: magnitude / perUnit, rest: magnitude % perUnit };
}
/**
 * Gaju for a person to read.
 *
 * From one coin up, always two decimals ("1,000.00", "99.75"). Below one coin,
 * up to six decimals ("0.002"), and dust under a millionth says "<0.000001"
 * rather than printing "0" for money that is really there.
 * Never rounds up, so a screen cannot promise more than the ledger holds.
 */
export function formatGaju(aettos) {
    const { negative, whole, rest } = split(aettos, MINOR_PER_UNIT.GAJU);
    const sign = negative ? '-' : '';
    if (whole > 0n || rest === 0n) {
        const cents = divideFloor(rest, 10n ** BigInt(DECIMALS.GAJU - 2))
            .toString()
            .padStart(2, '0');
        return `${sign}${withThousandsSeparators(whole.toString())}.${cents}`;
    }
    // Below one coin: up to six decimals, floored; dust under a millionth says so.
    const fraction = divideFloor(rest, 10n ** BigInt(DECIMALS.GAJU - 6))
        .toString()
        .padStart(6, '0')
        .replace(/0+$/, '');
    return fraction === '' ? `${sign}<0.000001` : `${sign}0.${fraction}`;
}
/** Dollars from USDC minor units: `$12.40`. Always two decimal places. */
export function formatUsd(usdcMinor, decimals = 2) {
    const { negative, whole, rest } = split(usdcMinor, MINOR_PER_UNIT.USDC);
    const scale = 10n ** BigInt(6 - decimals);
    const fraction = divideFloor(rest, scale).toString().padStart(decimals, '0');
    const body = `${withThousandsSeparators(whole.toString())}${decimals > 0 ? `.${fraction}` : ''}`;
    return `${negative ? '-' : ''}$${body}`;
}
/** A price, as the product always states it: `$12.40 per 1,000,000 Gaju`. */
export function formatPrice(value) {
    return `${formatUsd(value)} per ${withThousandsSeparators(PRICE_UNIT_GAJU.toString())} Gaju`;
}
/** Just the dollar figure of a price, for a converter's small print. */
export function formatPriceAmount(value) {
    return formatUsd(value);
}
/**
 * Parsing what a person typed.
 *
 * Accepts thousands separators, spaces and a leading currency sign. Refuses
 * anything else rather than guessing, and refuses more decimal places than the
 * asset has, because silently rounding someone's input is how "I typed 12.405
 * and it charged me 12.40" complaints happen.
 */
export class ParseError extends Error {
}
function parseDecimal(input, decimals, allowSign) {
    let text = input.trim().replace(/[,\s_]/g, '');
    if (allowSign !== '' && text.startsWith(allowSign)) {
        text = text.slice(allowSign.length);
    }
    const match = /^(\d+)(?:\.(\d*))?$/.exec(text);
    if (match === null) {
        throw new ParseError('Enter a number');
    }
    // Group 1 is mandatory in the pattern, so it is always present on a match.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const whole = match[1];
    const fraction = match[2] ?? '';
    if (fraction.length > decimals) {
        throw new ParseError(`Up to ${decimals} decimal places`);
    }
    const scaled = whole + fraction.padEnd(decimals, '0');
    return BigInt(scaled);
}
/** "1,000" or "1000.5" of Gaju to aettos. */
export function parseGaju(input) {
    return parseDecimal(input, 18, '');
}
/** "$12.40", "12.4" or "1,234.56" to USDC minor units. */
export function parseUsd(input) {
    return parseDecimal(input, 6, '$');
}
/**
 * A price as dollars per single Gaju: `$0.0000124`.
 *
 * Prices are stored per 1,000,000 Gaju because per-Gaju values are tiny. When a
 * person wants the per-coin figure, show it with as many decimals as it needs
 * and no trailing zeros, floored so it never overstates.
 */
export function formatUsdPerGaju(value) {
    // value is USDC minor units per 1,000,000 Gaju; per Gaju in dollars is
    // value / 10^6 (minor units) / 10^6 (million), i.e. 12 decimal places.
    const whole = value / 1000000000000n;
    const fraction = (value % 1000000000000n).toString().padStart(12, '0').replace(/0+$/, '');
    return `$${whole.toLocaleString('en-US')}${fraction === '' ? '' : `.${fraction}`}`;
}
/**
 * "$0.0000124" per single Gaju to USDC minor units per 1,000,000 Gaju.
 *
 * Twelve decimal places: six for USDC's minor units, six more for the million.
 * The result is exactly the unit prices are stored in, with no rounding.
 */
export function parseUsdPerGaju(input) {
    return parseDecimal(input, 12, '$');
}
//# sourceMappingURL=format.js.map